import { Box, Typography } from "@mui/material";
import { Suspense } from "react";

import { SCENES } from "../scenes";
import { colors } from "../theme/tokens";

import type { WidgetProps } from "./types";

// One vendored site scene on its own, full content width (capped), with
// optional noun labels laid over it and a one-line caption under it. Built
// 2026-09-08 for the How We Partner hero (the homepage journey scene): a
// prospective-supplier review asked for three labels, nouns only, sized for
// a phone, so the labels are HTML pills positioned by percent of the scene
// box rather than text inside the vendored SVG (which is owned by the site
// and never edited here). Percentages are of the rendered box, so they hold
// at every width because the SVG keeps its viewBox aspect.
export function Scene({ widget }: WidgetProps<"scene">) {
    const { scene, maxWidth = 800, labels = [], caption } = widget.props;
    const Component = SCENES[scene];

    return (
        <Box sx={{ maxWidth, mx: "auto" }}>
            <Box className="dr-scene" sx={{ position: "relative", width: "100%" }}>
                <Suspense
                    fallback={
                        <Box sx={{ aspectRatio: "1 / 1", borderRadius: "10px", bgcolor: colors.lightGray }} />
                    }
                >
                    <Component className="w-full" />
                </Suspense>
                {labels.map((label) => (
                    <Box
                        key={label.text}
                        sx={{
                            position: "absolute",
                            left: `${label.x}%`,
                            top: `${label.y}%`,
                            transform: "translate(-50%, -50%)",
                            px: 1.25,
                            py: 0.5,
                            borderRadius: "999px",
                            border: 1,
                            borderColor: colors.line,
                            bgcolor: colors.white,
                            color: colors.navy,
                            fontSize: { xs: "0.7rem", sm: "0.8rem" },
                            fontWeight: 700,
                            lineHeight: 1.2,
                            whiteSpace: "nowrap",
                            boxShadow: "0 1px 2px rgba(0, 31, 56, 0.08)",
                            pointerEvents: "none",
                        }}
                    >
                        {label.text}
                    </Box>
                ))}
            </Box>
            {caption && (
                <Typography
                    variant="body1"
                    sx={{ color: "text.secondary", fontSize: "1.05rem", textAlign: "center", mt: 2 }}
                >
                    {caption}
                </Typography>
            )}
        </Box>
    );
}
